// TAPINTO® Editor Frontend - Version History Component
// Version: 1.0
// Date: 2025-10-01

import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  User, 
  RotateCcw, 
  Eye, 
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useEditorStore } from '../../stores/editorStore';
import { PageVersion } from '../../types';
import { formatRelativeTime } from '../../utils/helpers';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

interface VersionHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  pageId: string;
}

export function VersionHistory({ isOpen, onClose, pageId }: VersionHistoryProps) {
  const { currentPage, loadPage } = useEditorStore();
  const [versions, setVersions] = useState<PageVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<PageVersion | null>(null);

  useEffect(() => {
    if (isOpen && pageId) {
      loadVersions();
    }
  }, [isOpen, pageId]);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const response = await apiService.getPageVersions(pageId);
      if (response.success && response.data) {
        setVersions(response.data.versions);
      } else {
        toast.error('Failed to load version history');
      }
    } catch (error) {
      console.error('Load versions error:', error);
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreVersion = async (version: PageVersion) => {
    if (!window.confirm(`Are you sure you want to restore version ${version.version}? This will create a new version with the restored content.`)) {
      return;
    }

    setRestoring(version.version_id);
    try {
      const response = await apiService.restorePageVersion(pageId, version.version);
      if (response.success) {
        toast.success('Version restored successfully');
        // Reload the current page
        await loadPage(pageId);
        // Reload versions
        await loadVersions();
        onClose();
      } else {
        toast.error(response.error || 'Failed to restore version');
      }
    } catch (error) {
      console.error('Restore version error:', error);
      toast.error('Failed to restore version');
    } finally {
      setRestoring(null);
    }
  };

  const handlePreviewVersion = (version: PageVersion) => {
    setSelectedVersion(version);
  };

  const getVersionIcon = (version: PageVersion) => {
    if (version.version === 1) {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    return <Clock className="h-4 w-4 text-neutral-500" />;
  };

  const getVersionStatus = (version: PageVersion) => {
    if (version.version === 1) {
      return 'Initial version';
    }
    if (version.change_description?.includes('Auto-save')) {
      return 'Auto-saved';
    }
    if (version.change_description?.includes('Manual save')) {
      return 'Manual save';
    }
    if (version.change_description?.includes('Restored')) {
      return 'Restored';
    }
    return 'Updated';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Version History"
      size="lg"
    >
      <div className="h-[600px] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">
                {currentPage?.title}
              </h3>
              <p className="text-sm text-neutral-500">
                Version history and restore points
              </p>
            </div>
            <Button variant="outline" onClick={loadVersions} disabled={loading}>
              Refresh
            </Button>
          </div>
        </div>

        {/* Versions List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <LoadingSpinner size="lg" />
            </div>
          ) : versions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Clock className="h-12 w-12 text-neutral-400 mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 mb-2">
                No versions found
              </h3>
              <p className="text-neutral-500">
                Version history will appear here as you make changes
              </p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-200">
              {versions.map((version, index) => (
                <div
                  key={version.version_id}
                  className="p-4 hover:bg-neutral-50 transition-colors"
                >
                  <div className="flex items-start space-x-3">
                    {/* Version Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {getVersionIcon(version)}
                    </div>

                    {/* Version Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-sm font-medium text-neutral-900">
                          Version {version.version}
                        </h4>
                        <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-1 rounded">
                          {getVersionStatus(version)}
                        </span>
                        {version.version === currentPage?.version && (
                          <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                            Current
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-neutral-600 mb-2">
                        {version.change_description || 'No description'}
                      </p>

                      <div className="flex items-center space-x-4 text-xs text-neutral-500">
                        <div className="flex items-center space-x-1">
                          <User className="h-3 w-3" />
                          <span>{version.snapshot_by}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="h-3 w-3" />
                          <span>{formatRelativeTime(version.snapshot_at)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreviewVersion(version)}
                        className="p-2"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      
                      {version.version !== currentPage?.version && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRestoreVersion(version)}
                          disabled={restoring === version.version_id}
                          className="p-2 text-blue-600 hover:text-blue-700"
                        >
                          {restoring === version.version_id ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <RotateCcw className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-neutral-200 bg-neutral-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-neutral-500">
              {versions.length} version{versions.length !== 1 ? 's' : ''} available
            </p>
            <Button onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Version Preview Modal */}
      {selectedVersion && (
        <Modal
          isOpen={!!selectedVersion}
          onClose={() => setSelectedVersion(null)}
          title={`Version ${selectedVersion.version} Preview`}
          size="xl"
        >
          <div className="p-6">
            <div className="bg-neutral-50 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-neutral-900 mb-2">
                Version Details
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-neutral-500">Version:</span>
                  <span className="ml-2 font-medium">{selectedVersion.version}</span>
                </div>
                <div>
                  <span className="text-neutral-500">Created:</span>
                  <span className="ml-2">{formatRelativeTime(selectedVersion.snapshot_at)}</span>
                </div>
                <div>
                  <span className="text-neutral-500">By:</span>
                  <span className="ml-2">{selectedVersion.snapshot_by}</span>
                </div>
                <div>
                  <span className="text-neutral-500">Status:</span>
                  <span className="ml-2">{getVersionStatus(selectedVersion)}</span>
                </div>
              </div>
              {selectedVersion.change_description && (
                <div className="mt-3">
                  <span className="text-neutral-500">Description:</span>
                  <p className="mt-1 text-sm">{selectedVersion.change_description}</p>
                </div>
              )}
            </div>

            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <h4 className="font-medium text-neutral-900 mb-2">
                Structure Preview
              </h4>
              <pre className="text-xs text-neutral-600 bg-neutral-50 p-3 rounded overflow-auto max-h-64">
                {JSON.stringify(selectedVersion.structure, null, 2)}
              </pre>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setSelectedVersion(null)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  handleRestoreVersion(selectedVersion);
                  setSelectedVersion(null);
                }}
                disabled={restoring === selectedVersion.version_id}
              >
                {restoring === selectedVersion.version_id ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Restore This Version
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
